import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link, Navigate } from "react-router-dom";
import { Mail, Phone, RefreshCw, LogOut, Send } from "lucide-react";
import { toast } from "sonner";
import { useAuth, authHeaders } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n || 0);

export default function Admin() {
  const { user, token, checking, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingSummary, setSendingSummary] = useState(false);

  const sendSummary = async () => {
    setSendingSummary(true);
    try {
      const { data } = await axios.post(`${API}/leads/weekly-summary`, null, { headers: authHeaders(token) });
      if (data.sent) {
        toast.success(`Summary emailed — ${data.leads_last_7_days} new lead(s) this week, ${data.total_leads} total.`);
      } else {
        toast.error("Summary could not be sent. Check email configuration.");
      }
    } catch (e) {
      console.error("Failed to send summary:", e);
      toast.error("Summary could not be sent.");
    } finally {
      setSendingSummary(false);
    }
  };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/leads`, { headers: authHeaders(token) });
      setLeads(data);
    } catch (e) {
      // 401 will be caught by AuthProvider when token expires
      console.error("Failed to load leads:", e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center text-charcoal">Loading…</div>;
  }
  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div data-testid="admin-page" className="min-h-screen bg-ivory">
      <div className="border-b border-ivory-400 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="font-serif text-2xl text-forest-900">
            Incent<span className="text-brass">Tax</span> <span className="text-charcoal-light text-base">/ admin</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-charcoal-light text-sm hidden sm:inline">{user.email}</span>
            <button
              onClick={sendSummary}
              disabled={sendingSummary}
              className="inline-flex items-center gap-2 bg-brass hover:bg-brass-500 text-forest-900 px-5 py-2.5 text-sm disabled:opacity-60"
              data-testid="admin-send-summary"
            >
              <Send size={14} /> {sendingSummary ? "Sending…" : "Email Summary"}
            </button>
            <button
              onClick={load}
              className="inline-flex items-center gap-2 bg-forest-900 hover:bg-forest-800 text-ivory px-5 py-2.5 text-sm"
              data-testid="admin-refresh"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 border border-ivory-400 hover:bg-ivory-200 text-charcoal px-5 py-2.5 text-sm"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-8 py-10">
        <div className="flex items-end justify-between mb-8">
          <h1 className="font-serif text-4xl text-forest-900">Leads</h1>
          <div className="text-charcoal-light text-sm">
            <span data-testid="lead-count">{leads.length}</span> total
          </div>
        </div>

        {loading ? (
          <div className="py-20 text-center text-charcoal-light">Loading…</div>
        ) : leads.length === 0 ? (
          <div className="py-20 text-center text-charcoal-light border border-ivory-400 bg-white">
            No leads yet.
          </div>
        ) : (
          <div className="space-y-4">
            {leads.map((l) => (
              <div
                key={l.id}
                data-testid={`lead-row-${l.id}`}
                className="bg-white border border-ivory-400 p-6 grid md:grid-cols-12 gap-4"
              >
                <div className="md:col-span-4">
                  <div className="font-serif text-2xl text-forest-900">{l.name}</div>
                  <div className="mt-2 flex flex-col gap-1 text-sm text-charcoal">
                    <a href={`mailto:${l.email}`} className="inline-flex items-center gap-2 hover:text-brass">
                      <Mail size={14} /> {l.email}
                    </a>
                    {l.phone && (
                      <a href={`tel:${l.phone}`} className="inline-flex items-center gap-2 hover:text-brass">
                        <Phone size={14} /> {l.phone}
                      </a>
                    )}
                  </div>
                </div>
                <div className="md:col-span-3 text-sm text-charcoal">
                  <div><span className="uppercase tracking-[0.18em] text-xs text-charcoal-light">Firm</span><br />{l.firm || "—"}</div>
                  <div className="mt-3"><span className="uppercase tracking-[0.18em] text-xs text-charcoal-light">Audience</span><br />{l.audience || "—"}</div>
                </div>
                <div className="md:col-span-2 text-sm text-charcoal">
                  <span className="uppercase tracking-[0.18em] text-xs text-charcoal-light">Service</span>
                  <div>{l.service_interest}</div>
                  {l.estimator_snapshot && (
                    <div className="mt-3">
                      <span className="uppercase tracking-[0.18em] text-xs text-charcoal-light">Est. Savings</span>
                      <div className="text-brass font-medium">
                        {fmt(l.estimator_snapshot.estimated_cash_savings)}
                      </div>
                    </div>
                  )}
                </div>
                <div className="md:col-span-3 text-sm text-charcoal">
                  <span className="uppercase tracking-[0.18em] text-xs text-charcoal-light">Message</span>
                  <div className="line-clamp-4 whitespace-pre-wrap">{l.message || "—"}</div>
                  <div className="mt-2 text-xs text-charcoal-light">
                    {new Date(l.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
